import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MapService } from "../../../../services/map.service";
import { Device } from "../../../../model/device";
import { DevicesService } from "../../../../services/devices.service";
import { Asset } from "../../../../model/asset";
import { MessageService } from "primeng/api";
import { DeviceProperty } from "../../../../model/device-property";
import { DeviceMeasurement } from "../../../../model/device-measurement";
import { IntegrationModule } from "../../../../model/integration-module";
import * as mapboxgl from 'mapbox-gl';
import { CommonMessagesService } from "../../../../services/common-messages.service";
import {AuthGuard} from '../../../../common/auth-guard.service';
import {finalize} from 'rxjs/operators';

@Component({
  selector: 'app-device-overview',
  templateUrl: './device-overview.component.html',
  styleUrls: ['./device-overview.component.scss']
})
export class DeviceOverviewComponent implements OnInit, OnDestroy {

  public readonly DEFAULT_ZOOM = 14;

  @Input()
  public device: Device;

  public asset: Asset;
  public properties: DeviceProperty[];
  public availableData: {total: number, today: number, allTime: number};

  public marker;

  public integrationModules: IntegrationModule[];
  public selectedModule: IntegrationModule;
  public currentModuleInfo: any;
  public loadingAssetDevices: boolean;
  public loadingIntegrationModules: boolean;
  public loadedMap: boolean;

  public lastCommunicationFromIntModules: number;

  public map: mapboxgl.Map;

  constructor(private mapService: MapService,
              private devicesService: DevicesService,
              private changeDetectorRef: ChangeDetectorRef,
              private commonMessagesService: CommonMessagesService,
              private messageService: MessageService,
              public authGuard: AuthGuard) {}

  ngOnInit(): void {
    this.loadDeviceAssets();
    this.loadDeviceProperties();
    this.loadAvailableData();
    this.loadIntegrationModules();
  }

  private loadDeviceAssets() {
    this.loadingAssetDevices = true;
    this.loadedMap = false;
    this.devicesService.getDeviceAssets(this.device.uuid).subscribe(response => {
      if(response.assetDevices.length > 1) {
        this.showMultipleAssetsWarning();
      }
      if(response.assetDevices.length > 0) {
        this.asset = response.assetDevices[0].asset;
        this.changeDetectorRef.detectChanges();
        this.buildAndConfigureMap();
      }
      this.loadingAssetDevices = false;
      this.loadedMap = true;
    });
  }

  private loadDeviceProperties() {
    this.devicesService.getDeviceProperties(this.device.uuid).subscribe(response => {
      this.properties = response.deviceProperties;
    });
  }

  private loadAvailableData() {
    this.devicesService.getDeviceMeasurementsOfDevice(this.device.uuid).toPromise().then(response => {
      this.buildAvailableDataWidget(response.deviceMeasurements);
    });
  }

  private loadIntegrationModules() {
    this.loadingIntegrationModules = true;
    this.devicesService.getDeviceIntegrationModules(this.device.uuid)
      .pipe(finalize(() => this.loadingIntegrationModules = false))
      .subscribe(response => {
      this.integrationModules = response.integrationModules;
      this.lastCommunicationFromIntModules = this.getLastCommFromIntModules(this.integrationModules);
      this.setModuleAdditionalInfo(this.integrationModules[0]);
    });
  }

  private getLastCommFromIntModules(integrationModules: IntegrationModule[]): number {
    let lastCommTimestamp = 0;
    integrationModules.forEach(module => {
      if (module?.additionalInfo) {
        const addInfo = JSON.parse(module.additionalInfo);
        if (addInfo?.entryTimestamp && addInfo.entryTimestamp > lastCommTimestamp) {
          lastCommTimestamp = addInfo.entryTimestamp;
        }
      }
    });
    return lastCommTimestamp;
  }

  private buildAvailableDataWidget(deviceMeasurements: DeviceMeasurement[]) {
    let todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);

    let today = 0, allTime = 0;
    deviceMeasurements.forEach(measurement => {
      if(measurement.dateLastValueReported) {
        let lastDate = new Date(measurement.dateLastValueReported);
        allTime++;
        if(lastDate >= todayDate) {
          today++;
        }
      }
    });

    this.availableData = {
      total: deviceMeasurements.length,
      today: today,
      allTime: allTime
    };
  }

  private buildAndConfigureMap() {
    let latitude = this.asset.latitude;
    let longitude = this.asset.longitude;

    this.map = this.mapService.buildMap(this.DEFAULT_ZOOM, latitude, longitude);

    this.mapService.buildStaticPulsingDot(this.map, latitude, longitude);
  }

  setModuleAdditionalInfo(module) {
    this.selectedModule = module;
    if(module?.additionalInfo) {
      this.currentModuleInfo = JSON.parse(module.additionalInfo);
    } else {
      this.currentModuleInfo = undefined;
    }
  }

  showMultipleAssetsWarning() {
    this.messageService.add({
      severity: 'warn',
      summary: 'Multiple assets found',
      detail: 'Two or more active assets were found for this device'
    });
  }

  ngOnDestroy(): void {
    this.map?.remove();
  }

}
